"use client"
import { useScroll, useTransform, motion } from 'framer-motion'
import React, { useRef } from 'react'
import { cn } from '@/lib/utils'

interface AnimatedTextProps {
  text: string
  className?: string
  /** Framer Motion scroll offset start */
  offsetStart?: string
  /** Framer Motion scroll offset end */
  offsetEnd?: string
}

export default function AnimatedText({ 
  text, 
  className,
  offsetStart = "start 0.9",
  offsetEnd = "start 0.25"
}: AnimatedTextProps) {
  const container = useRef(null)
  const { scrollYProgress } = useScroll({
    target: container,
    offset: [offsetStart, offsetEnd] as ['start 0.9', 'start 0.25']
  })

  const words = text.split(" ")
  
  return (
    <div 
      ref={container}         
      className={cn("relative block whitespace-nowrap", className)}
    >
      {
        words.map((word, i) => {
          const start = i / words.length
          const end = start + (1 / words.length)
          return (
            <Word 
              key={i} 
              progress={scrollYProgress} 
              range={[start, end]}
            >
              {word}
            </Word>
          )
        })
      }
    </div>
  )
}

const Word = ({ children, progress, range }: {
  children: string
  progress: import('framer-motion').MotionValue<number>
  range: [number, number]
}) => {
  const amount = range[1] - range[0]
  const step = amount / children.length
  
  return (
    <span className="inline mr-[0.25em] last:mr-0">
      {
        children.split("").map((char, i) => {
          const start = range[0] + (i * step)
          const end = range[0] + ((i + 1) * step)
          return (
            <Char 
              key={`c_${i}`} 
              progress={progress} 
              range={[start, end]}
            >
              {char}
            </Char>
          )
        })
      }
    </span>
  )
}

const Char = ({ children, progress, range }: {
  children: string
  progress: import('framer-motion').MotionValue<number>
  range: [number, number]
}) => {
  const opacity = useTransform(progress, range, [0, 1])
  
  return (
    <span className="relative inline-block">
      <span className="text-background/5">{children}</span>
      <motion.span 
        className="absolute inset-0 text-background/50"
        style={{ opacity: opacity }}
      >
        {children}
      </motion.span>
    </span>
  )
}
